import { Injectable, NgZone } from "@angular/core";
import { IUrlParamsKey } from "../../interfaces/i-url-params-name";
import { IUrlParams } from "../../interfaces/i-url-params";
import { ActivatedRoute, Router } from "@angular/router";
import { DualCommunicatorService } from "./dual-communicator.service";
import { StatusService } from "./status.service";
import { EncodingService } from "./encoding.service";
import { ProjectStatus } from "../../model/project-status";
import { Viewerlayer } from "../../model/viewerlayer";
import { ITimelineFilter } from "../../interfaces/i-timeline-filter";

@Injectable({
  providedIn: "root",
})
export class UrlStateService {
  MASTER_MAP_ID = "map1";

  public urlParamsKey: IUrlParamsKey;

  private urlParams: IUrlParams = {
    token: null,
    latitude: null,
    longitude: null,
    zoom: null,
    address: null,
    state: null,
  };

  constructor(
    private ngZone: NgZone,
    protected router: Router,
    protected route: ActivatedRoute,
    protected dualViewerService: DualCommunicatorService,
  ) {}

  getStateParamKey(mapId: string): string {
    return `state${mapId?.replace("map", "")}`;
  }

  /**
   * Get saved url parameters
   */
  getSavedUrlParams(): IUrlParams {
    return this.urlParams;
  }

  /**
   * Save url parameters to compare with current version
   * @param urlParams
   */
  mergeUrlParams(urlParams: IUrlParams) {
    this.urlParams = { ...this.urlParams, ...urlParams };
  }

  getParamsFromURL() {
    this.urlParamsKey = this.getUrlParamsKey();

    return {
      latitude: this.route.snapshot.queryParamMap.has(this.urlParamsKey.latitudeURLKey)
        ? +this.route.snapshot.queryParamMap.get(this.urlParamsKey.latitudeURLKey)
        : null,
      longitude: this.route.snapshot.queryParamMap.has(this.urlParamsKey.longitudeURLKey)
        ? +this.route.snapshot.queryParamMap.get(this.urlParamsKey.longitudeURLKey)
        : null,
      zoom: this.route.snapshot.queryParamMap.has(this.urlParamsKey.zoomURLKey)
        ? +this.route.snapshot.queryParamMap.get(this.urlParamsKey.zoomURLKey)
        : null,
      address: this.route.snapshot.queryParamMap.get(this.urlParamsKey.addressURLKey),
      state1: this.route.snapshot.queryParamMap.get(this.urlParamsKey.state1URLKey),
      state2: this.route.snapshot.queryParamMap.get(this.urlParamsKey.state2URLKey),
    };
  }

  getUrlParamsKey(): IUrlParamsKey {
    const urlParamsKey = {
      latitudeURLKey: "latitude",
      longitudeURLKey: "longitude",
      zoomURLKey: "zoom",
      addressURLKey: "address",
      state1URLKey: "state1",
      state2URLKey: "state2",
    };
    const resultUrlParams = {};
    this.route.snapshot.queryParamMap.keys.map((key) => {
      const paramName = `${key.toLowerCase()}URLKey`;
      if (paramName in urlParamsKey) {
        resultUrlParams[paramName] = key;
      }
      return paramName;
    });
    return resultUrlParams;
  }

  // region User configuration Url/Save

  setLocationURL(center: number[], zoom: number) {
    const keys = this.getUrlParamsKey();

    // Default variables name from URL parameters;
    const latitudeURLKey = keys.latitudeURLKey || "latitude";
    const longitudeURLKey = keys.longitudeURLKey || "longitude";
    const zoomURLKey = keys.zoomURLKey || "zoom";
    const addressURLKey = keys.addressURLKey || "address";

    this.urlParams = {
      latitude: center[1],
      longitude: center[0],
      zoom,
    };

    const queryParams = {
      [latitudeURLKey]: center[1].toString(),
      [longitudeURLKey]: center[0].toString(),
      [zoomURLKey]: zoom ? zoom.toString() : "5",
    };

    if (addressURLKey) {
      this.urlParams.address = this.route.snapshot.queryParamMap.get(addressURLKey);
    } else {
      this.urlParams.address = null;
      queryParams.address = null;
    }

    return queryParams;
  }

  createStateParameter(
    projectStatus: ProjectStatus,
    mapMode: string,
    orientation: string,
    overlayLayers: Viewerlayer[],
    simplifyFilters: ITimelineFilter,
    isActivateBaseMap: boolean,
    mapId: string,
    infrared: boolean,
    measurementSystem: number,
  ): any {
    // discrete layer its deprecated now everything its related with collections and views
    const viewStatus = StatusService.mapStatusData(
      mapMode,
      orientation,
      overlayLayers,
      simplifyFilters,
      isActivateBaseMap,
      projectStatus?.centerAOI,
      infrared,
      measurementSystem,
    );

    const isMasterMap = mapId == this.MASTER_MAP_ID;
    // add swipe status only in first map
    if (isMasterMap && projectStatus?.mapStatus?.swipeStatus?.enable) {
      const swipeStatus = projectStatus?.mapStatus?.swipeStatus;
      viewStatus.swipe = {
        left: swipeStatus.left?.getLayerId(),
        right: swipeStatus.right?.getLayerId(),
      };
    }
    if (!this.dualViewerService.isDualEnabled()) {
      this.dualViewerService.mapStatus.clear();
    }
    this.dualViewerService.mapStatus.set(mapId, viewStatus);
    // add dual status only in the first map
    if (isMasterMap) {
      viewStatus.dual = this.dualViewerService.isDualEnabled();
    }
    // we have to stringify the status data to encode to base 64
    return viewStatus;
  }

  setStateURL(mapId: string) {
    const key = this.getStateParamKey(mapId);

    const state = this.dualViewerService.mapStatus.get(mapId);
    return this.setStateURLWithParams(key, state);
  }

  setStateURLWithParams(key, state) {
    if (state) {
      const param = {
        [key]: EncodingService.encodingB64(JSON.stringify(state)),
      };
      this.urlParams.state = { [key]: param[key] };
      return param;
    }

    return null;
  }

  getStateParam(mapId: string): any {
    const key = this.getStateParamKey(mapId);
    return this.getStateParamWithKey(key);
  }

  getStateParamWithKey(key): any {
    if (this.route.snapshot.queryParamMap.has(key)) {
      try {
        const stateParam = this.route.snapshot.queryParamMap.get(key);
        return JSON.parse(EncodingService.decodingB64(stateParam));
      } catch (e) {
        console.log("State param wrong formatted ");
      }
    }

    return;
  }

  /**
   * Update url query parameters
   *
   * @param params object {key:value,...}
   * If value is null remove this parameter
   */
  updateQueryParams(params) {
    this.ngZone.run(() => {
      this.router.navigate(["."], {
        relativeTo: this.route,
        queryParams: params,
        queryParamsHandling: "merge",
        state: { paramsFromMap: true },
      });
    });
  }
}
